/**
 * Student Portal — Return workflow.
 * Uses GET /api/students/my-returnables + POST /api/returns.
 */
import React, { useMemo, useState } from 'react';
import { RotateCcw, ShieldCheck } from 'lucide-react';
import { Card, EmptyState, StatusPill, Modal, returnStatusPill, ComponentMonogram } from '../ui';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';

const CONDITIONS = [
  { value: 'unused',    label: 'Unused',        hint: 'Sterile & untouched — can be re-issued after clerk verification.' },
  { value: 'used-good', label: 'Used · Good',   hint: 'Used but in good condition, suitable for cleaning & sterilisation.' },
  { value: 'damaged',   label: 'Damaged',       hint: 'Component is damaged or unfit for further clinical use.' },
  { value: 'lost',      label: 'Lost',          hint: 'Component cannot be located and cannot be returned physically.' },
];

const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString() : '—';

export default function ReturnsPage({ ctx }) {
  const { returnables, returns, refresh, showToast } = ctx;
  const [active, setActive] = useState(null); // { row }
  const [qty, setQty] = useState(1);
  const [condition, setCondition] = useState('used-good');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const outstanding = useMemo(
    () => returnables.filter(r => r.return_status === 'returnable' || r.return_status === 'partially_returned'),
    [returnables]
  );
  const pending = useMemo(
    () => returns.filter(r => r.status === 'pending' || r.status === 'clerk_verified'),
    [returns]
  );
  const history = useMemo(
    () => returns.filter(r => r.status === 'approved' || r.status === 'rejected'),
    [returns]
  );

  const openReturn = (row) => {
    setActive({ row });
    setQty(row.qty_outstanding);
    setCondition('used-good');
    setNotes('');
  };
  const closeReturn = () => { setActive(null); setSubmitting(false); };

  const submitReturn = async () => {
    if (!active) return;
    const { row } = active;
    const q = Number(qty);
    if (!q || q < 1 || q > row.qty_outstanding) {
      showToast(`Quantity must be between 1 and ${row.qty_outstanding}.`, 'bad');
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post(endpoints.returns.create, {
        return_photos: [],
        notes: notes || `Student-initiated return · ${row.order_code}`,
        items: [{
          component_id: row.component_id,
          quantity: q,
          condition,
          source_order_id: row.order_id,
          component_name: row.component_name,
          brand_name: row.brand_name,
          size: row.size,
        }],
      });
      showToast('Return submitted. The clerk will verify it next.', 'ok');
      closeReturn();
      await Promise.all([refresh.returns(), refresh.returnables()]);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not submit return.', 'bad');
      setSubmitting(false);
    }
  };

  const conditionMeta = CONDITIONS.find(c => c.value === condition);

  return (
    <>
      <div>
        <span className="sp-eyebrow">Portal</span>
        <h1 className="sp-page-title" data-testid="portal-returns-title">Return Components</h1>
        <p className="sp-page-lede">
          Log a component back to inventory. A clerk verifies the item physically before it is credited to your record.
        </p>
      </div>

      {/* Returnable items */}
      <Card
        title="Ready to Return"
        subtitle="Currently issued to you and eligible for return."
        testId="returns-outstanding-card"
      >
        {outstanding.length === 0 ? (
          <EmptyState
            icon={ShieldCheck}
            title="Nothing pending return"
            message="You have no components currently outstanding. Any newly-issued returnable items will appear here."
            testId="returns-outstanding-empty"
          />
        ) : (
          <div className="sp-tile-grid" data-testid="returns-outstanding-grid">
            {outstanding.map(row => (
              <article key={`${row.order_id}-${row.component_id}`} className="sp-tile" data-testid={`return-row-${row.order_id}-${row.component_id}`}>
                <ComponentMonogram brand={row.brand_name} name={row.component_name} />
                <div className="sp-tile-body">
                  <h4 className="sp-tile-name">{row.component_name}</h4>
                  <div className="sp-tile-meta">
                    <span><strong>{row.brand_name || '—'}</strong></span>
                    {row.size && <span>{row.size}</span>}
                    <span className="sp-subtle">{row.order_code}</span>
                  </div>
                  <div className="sp-tile-meta">
                    <span>Issued <strong className="sp-mono-num">{row.qty_issued}</strong></span>
                    <span>Returned <strong className="sp-mono-num">{row.qty_returned || 0}</strong></span>
                    <span>Outstanding <strong className="sp-mono-num">{row.qty_outstanding}</strong></span>
                  </div>
                  {row.return_due_date && (
                    <div className="sp-mt-2"><StatusPill variant="warn">Due {formatDate(row.return_due_date)}</StatusPill></div>
                  )}
                </div>
                <div className="sp-tile-action">
                  <button
                    type="button"
                    className="sp-btn sp-btn-sm sp-btn-accent"
                    onClick={() => openReturn(row)}
                    data-testid={`return-open-btn-${row.order_id}-${row.component_id}`}
                  >
                    <RotateCcw size={13} /> Return
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </Card>

      {/* Pending returns */}
      <Card
        title="Awaiting Clerk Verification"
        subtitle="Return requests submitted, waiting for the clerk (and then faculty) to verify."
        testId="returns-pending-card"
      >
        {pending.length === 0 ? (
          <p className="sp-muted" style={{ margin: 0, fontSize: 13 }}>No return requests are currently in progress.</p>
        ) : (
          <div data-testid="returns-pending-list">
            {pending.map(r => {
              const pill = returnStatusPill(r.status);
              return (
                <div key={r.id} className="sp-item-line" style={{ padding: '12px 0', borderBottom: '1px solid var(--diis-hairline)' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13 }}>
                      <strong>RET-{r.id.slice(-6).toUpperCase()}</strong>
                      <span className="sp-subtle" style={{ marginLeft: 8 }}>
                        {(r.items || []).map(i => `${i.component_name || 'Component'} × ${i.quantity}`).join(', ')}
                      </span>
                    </div>
                    <div className="sp-subtle" style={{ marginTop: 2 }}>{new Date(r.created_at).toLocaleString()}</div>
                  </div>
                  <StatusPill variant={pill.variant}>{pill.label}</StatusPill>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Return history */}
      <Card title="Return History" subtitle="Completed and rejected returns." testId="returns-history-card">
        {history.length === 0 ? (
          <p className="sp-muted" style={{ margin: 0, fontSize: 13 }}>No completed returns yet.</p>
        ) : (
          <div data-testid="returns-history-list">
            {history.map(r => {
              const pill = returnStatusPill(r.status);
              return (
                <div key={r.id} className="sp-item-line" style={{ padding: '12px 0', borderBottom: '1px solid var(--diis-hairline)' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13 }}>
                      <strong>RET-{r.id.slice(-6).toUpperCase()}</strong>
                      <span className="sp-subtle" style={{ marginLeft: 8 }}>
                        {(r.items || []).map(i => `${i.component_name || 'Component'} × ${i.quantity} (${i.condition})`).join(', ')}
                      </span>
                    </div>
                    <div className="sp-subtle" style={{ marginTop: 2 }}>{new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                  <StatusPill variant={pill.variant}>{pill.label}</StatusPill>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Return modal */}
      <Modal
        open={!!active}
        onClose={closeReturn}
        title="Return Component"
        subtitle={active ? `${active.row.component_name} · ${active.row.order_code}` : ''}
        testId="return-modal"
        footer={(
          <>
            <button type="button" className="sp-btn sp-btn-ghost" onClick={closeReturn} data-testid="return-modal-cancel">Cancel</button>
            <button
              type="button"
              className="sp-btn sp-btn-accent"
              disabled={submitting}
              onClick={submitReturn}
              data-testid="return-modal-submit"
            >{submitting ? 'Submitting…' : 'Submit Return'}</button>
          </>
        )}
      >
        {active && (
          <>
            <div style={{ fontSize: 13, color: 'var(--diis-ink-muted)', marginBottom: 12 }}>
              You are returning up to <strong className="sp-mono-num" style={{ color: 'var(--diis-ink)' }}>{active.row.qty_outstanding}</strong> unit(s) of <strong>{active.row.component_name}</strong>{active.row.size ? ` (${active.row.size})` : ''} from request {active.row.order_code}.
            </div>

            <div className="sp-grid-2">
              <div>
                <label className="sp-field-label">Quantity to return</label>
                <input
                  type="number"
                  className="sp-input"
                  min={1}
                  max={active.row.qty_outstanding}
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  data-testid="return-qty-input"
                />
                <div className="sp-subtle sp-mt-2">Outstanding: {active.row.qty_outstanding}</div>
              </div>
              <div>
                <label className="sp-field-label">Condition on return</label>
                <select
                  className="sp-select"
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                  data-testid="return-condition-select"
                >
                  {CONDITIONS.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
                {conditionMeta && (
                  <div className="sp-subtle sp-mt-2">{conditionMeta.hint}</div>
                )}
              </div>
            </div>

            <div className="sp-mt-4">
              <label className="sp-field-label">Notes (optional)</label>
              <textarea
                className="sp-textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything the clerk should know about this return."
                data-testid="return-notes-input"
              />
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
